import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import {
  Box,
  Card,
  CheckboxGroup,
  CreateCSSProperties,
  FilterPanel,
  FormLabel,
  Grid,
  Loader,
  makeAppStyles,
  MonthPickerProps,
  ProviderLogo,
  RadioFormGroup,
  Tab,
  Tabs,
  Typography,
  useAppTheme,
  useBreakpoint,
} from '@percept/mui'

import {
  ProviderBenchmarkAvailability,
  ProviderBenchmarks,
  ProviderBenchmarksHookProps,
  useAllProviderBenchmarks,
  useDocumentTitle,
  useProviderBenchmarks,
  useProviderBenchmarkAvailability,
  useUrlState,
} from '@percept/hooks'

import { UserPrivilegeContext } from '@percept/app-components'

import { MarketDisplayLabel } from 'components/MarketDisplay'

import { DefaultDatumRenderer, ExportableBenchmarkQuadrantChart, getMarketTooltipContent, MarketDatumRenderer } from './BenchmarkingQuadrantChart'

import { BenchmarkingTable } from './BenchmarkingTable'

import { TimeComparisonFilterInput } from 'components/SmartCampaignAssessment'

import { capitalize, every, flatten, get, identity, some, uniq } from 'lodash-es'

import { max, min } from 'date-fns'

import { dateFnsOps, formatUTC, getPrivilegedOrgUnitIds, isoDateUTC, produceKeyedMapping } from '@percept/utils'

import { VODAFONE_GLOBAL_ID, VodafoneMarket, vodafoneMarkets } from 'vodafoneMarkets'

import { defaultProviderSegmentMap, getBenchmarkDatasets, getSegmentLabel } from './lib'

import { getBenchmarkChartData, getBenchmarkingTableRows, getDatumLabel, segmentExclusionMap } from './utils'

import { Breakpoint } from '@material-ui/core/styles/createBreakpoints'

import { Nullable, OrgPrivileges, ReportProviderV2 } from '@percept/types'

import { providerLabelMap } from '@percept/constants'

import { UseQueryResult } from 'react-query'


// NOTE - revert to this when API is available
const benchmarkingProviders: ReportProviderV2[] = [
  'GOOGLE_ADS',
  'FACEBOOK',
  'DV360',
  'ADFORM',
  'TIKTOK',
]

const vodafoneMarketsById = produceKeyedMapping(vodafoneMarkets, 'id')

const useTabStyles = makeAppStyles( theme => ({
  tabs: {
    margin: theme.spacing(4,0),
    borderBottom: `1px solid ${theme.palette.text.hint}`,
  },
  tab: {
    textTransform: 'none',
  },
}))


type BasicSelectProps<T> = {
  value: T
  onChange: (value: T) => void
}


const BenchmarkingTabs = ({
  value,
  onChange,
}: BasicSelectProps<ReportProviderV2>): JSX.Element => {
  const classes = useTabStyles()
  return (
    <Tabs
      indicatorColor='primary'
      textColor='primary'
      variant='fullWidth'
      className={classes.tabs}
      value={value}
      onChange={(_, value: ReportProviderV2): void => {
        onChange(value)
      }}>
      { benchmarkingProviders.map( provider => (
        <Tab
          key={`tab-${provider}`}
          className={classes.tab}
          value={provider}
          label={
            <ProviderLogo
              size={1.5}
              provider={provider} />
          } />
      ))}
    </Tabs>
  )
}


type BenchmarkingStateBase = {
  baselineStart: string | null
  baselineEnd: string | null
  comparisonStart: string | null
  comparisonEnd: string | null
}

type ChannelViewBenchmarkingState = (
  BenchmarkingStateBase & {
    view: 'channel'
    provider: ReportProviderV2
    segment: string
    orgUnitIds: string[]
    orgUnitId?: never
  }
)

type MarketViewBenchmarkingState = (
  BenchmarkingStateBase & {
    view: 'market'
    provider?: never
    segment?: never
    orgUnitIds?: never
    orgUnitId: string | null
  }
)

type BenchmarkingState = ChannelViewBenchmarkingState | MarketViewBenchmarkingState


type TimePeriods = Record<'baseline' | 'comparison', [Date | null, Date | null]>


type FilterStateExtension<T extends object> = (
  Omit<
    T,
    | 'baselineStart'
    | 'baselineEnd'
    | 'comparisonStart'
    | 'comparisonEnd'
  > & {
    timePeriods: TimePeriods | null
  }
)

type ChannelViewBenchmarkingFilters = FilterStateExtension<ChannelViewBenchmarkingState>
type MarketViewBenchmarkingFilters = FilterStateExtension<MarketViewBenchmarkingState>

type BenchmarkingFilters = ChannelViewBenchmarkingFilters | MarketViewBenchmarkingFilters

const breakpointChartHeights: Record<Breakpoint, number> = {
  xs: 300,
  sm: 300,
  md: 300,
  lg: 400,
  xl: 500,
}

const useStyles = makeAppStyles( theme => ({
  title: {
    fontSize: 38,
    lineHeight: 1.125,
    fontWeight: 700,
    marginBottom: theme.spacing(4),
  },
  tab: {
    textTransform: 'none',
  },
  filterCard: {
    maxWidth: '100%',
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    boxSizing: 'border-box',
  },
  filterGroup: {
    minWidth: 'max-content',
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  monthRangeInput: {
    marginRight: theme.spacing(2),
  },
  monthRangeSubheader: {
    display: 'flex',
    flexBasis: '100%',
    justifyContent: 'center',
    fontSize: 13,
    marginBottom: theme.spacing(1),
  },
  filterDivider: {
    margin: theme.spacing(0, 4),
  },
  separator: {
    margin: theme.spacing(0, 0.75),
    color: theme.palette.text.disabled,
  },
  filterItem: {
    marginRight: theme.spacing(1.5),
    ':last-child': {
      marginRight: 0,
    },
  },
  datasetLabel: {
    marginLeft: 30,
    minWidth: 'max-content',
    [theme.breakpoints.down('md')]: {
      fontSize: 20,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
  },
  gridContainer: {
    marginTop: theme.spacing(3),
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 4, 4, 2.5),
  },
  chart: {
    ...(Object.entries(breakpointChartHeights).reduce( (acc, [breakpoint, size]) => {
      acc[theme.breakpoints.only(breakpoint as Breakpoint)] = {
        width: size,
      }
      return acc
    }, {} as CreateCSSProperties)),
  },
  tableGridItem: {
    flexGrow: 1,
  },
}))


const breakpointMarkerSizes: Record<Breakpoint, number> = {
  xs: 16,
  sm: 16,
  md: 16,
  lg: 20,
  xl: 24,
}


const getAvailableDateRange = (
  availability: ProviderBenchmarkAvailability[] | undefined
): Nullable<Record<'minDate' | 'maxDate', Date>> => {
  let minDates: Date[] = []
  let maxDates: Date[] = []
  if( availability ){
    // For now, we're just assigning an overall range based on
    // the comparable range of dates across all providers and variants
    const monthPeriods = flatten(
      availability.map(
        a => a.available_periods.filter(
          p => {
            const excludedSegments = segmentExclusionMap[a.provider]
            return (!excludedSegments || !excludedSegments.includes(p.variant))
          }
        ).map(
          p => ({
            minDate: new Date(p.start_date),
            maxDate: new Date(p.end_date)
          })
        )
      )
    )
    minDates = monthPeriods.map( p => p.minDate )
    maxDates = monthPeriods.map( p => p.maxDate )
  }

  return {
    minDate: minDates.length ? max(minDates) : null,
    maxDate: maxDates.length ? min(maxDates) : null,
  }
}


const dateToString = (date: Date | null): string | null => (
  date && isoDateUTC(dateFnsOps.startOfMonth(date))
)

const stringToDate = (date: string | null, cast: ((d: Date) => Date) = identity): Date | null => (
  date ? cast(new Date(date)) : null
)

const nullableDateRangeToDisplayString = (dateRange: [Date | null, Date | null]): string | null => (
  dateRange[0] && dateRange[1] && (
    uniq(
      [formatUTC(dateRange[0], 'MMM yy'), formatUTC(dateRange[1], 'MMM yy')]
    ).join(' - ')
  )
)

const timePeriodsToPageTitleFragment = (timePeriods: TimePeriods | null): string | null => {
  if( !timePeriods ){
    return null
  }
  if( !every([...timePeriods.baseline, ...timePeriods.comparison]) ){
    return null
  }
  return [
    nullableDateRangeToDisplayString(timePeriods.comparison),
    'vs',
    nullableDateRangeToDisplayString(timePeriods.baseline),
  ].join(' ')
}

const urlStateToBenchmarkingFilters = (urlState: BenchmarkingState): BenchmarkingFilters => {
  const timePeriods: TimePeriods = {
    baseline: [
      stringToDate(urlState.baselineStart, dateFnsOps.startOfMonth),
      stringToDate(urlState.baselineEnd, dateFnsOps.startOfMonth),
    ],
    comparison: [
      stringToDate(urlState.comparisonStart, dateFnsOps.startOfMonth),
      stringToDate(urlState.comparisonEnd, dateFnsOps.startOfMonth),
    ],
  }
  if( urlState.view === 'channel' ){
    return {
      view: 'channel',
      provider: urlState.provider || 'GOOGLE_ADS',
      segment: urlState.segment,
      orgUnitIds: urlState.orgUnitIds,
      timePeriods,
    }  
  } 
  return {
    view: 'market',
    orgUnitId: urlState.orgUnitId,
    timePeriods,
  }
}

const benchmarkingFiltersToUrlState = (filters: BenchmarkingFilters): BenchmarkingState => {
  const timeParams = {
    baselineStart: dateToString(get(filters.timePeriods, ['baseline', 0]) || null),
    baselineEnd: dateToString(get(filters.timePeriods, ['baseline', 1]) || null),
    comparisonStart: dateToString(get(filters.timePeriods, ['comparison', 0]) || null),
    comparisonEnd: dateToString(get(filters.timePeriods, ['comparison', 1]) || null),
  }
  if( filters.view === 'channel' ){
    return {
      view: 'channel',
      provider: filters.provider || 'GOOGLE_ADS',
      segment: filters.segment || 'SEARCH',
      orgUnitIds: filters.orgUnitIds,
      orgUnitId: undefined,
      ...timeParams
    }
  }
  return {
    view: 'market',
    orgUnitId: filters.orgUnitId,
    orgUnitIds: undefined,
    provider: undefined,
    segment: undefined,
    ...timeParams,
  }
}


const getDefaultPeriod = (maxDate: Date | null): Record<'currentDate' | 'previousDate', Date | null> => {
  return {
    currentDate: maxDate && dateFnsOps.startOfMonth(maxDate),
    previousDate: maxDate && dateFnsOps.startOfMonth(dateFnsOps.subMonths(maxDate, 1)),
  }
}

const getComparisonParams = ({
  currentDate,
  previousDate,  
}: Record<'currentDate' | 'previousDate', Date | null>
): Pick<BenchmarkingState, 'baselineEnd' | 'baselineStart' | 'comparisonEnd' | 'comparisonStart'> => {
  return {
    comparisonStart: currentDate && isoDateUTC(currentDate),
    comparisonEnd: currentDate && isoDateUTC(currentDate),
    baselineStart: previousDate && isoDateUTC(previousDate),
    baselineEnd: previousDate && isoDateUTC(previousDate),
  }
}


const getViewableMarkets = (orgPrivileges: OrgPrivileges[]): VodafoneMarket[] => {
  const viewableIds = getPrivilegedOrgUnitIds(['benchmarking.view'], orgPrivileges)
  return vodafoneMarkets.filter( m => viewableIds.includes(m.id))
}


export const Benchmarking = (): JSX.Element => {

  const privileges = useContext(UserPrivilegeContext)

  const defaultOrgUnitId = privileges.user && privileges.user.default_org_unit_id

  const viewableMarkets = getViewableMarkets(privileges.org_privileges)

  const availabilityQuery = useProviderBenchmarkAvailability()

  const { minDate, maxDate } = useMemo(() => (
    getAvailableDateRange(availabilityQuery.data)
  ), [availabilityQuery.data])

  const defaultPeriod = getDefaultPeriod(maxDate)

  let defaultBenchmarkingState: BenchmarkingState = {
    ...getComparisonParams(defaultPeriod),
  } as BenchmarkingState
  if( defaultOrgUnitId === VODAFONE_GLOBAL_ID ){
    defaultBenchmarkingState = {
      ...defaultBenchmarkingState,
      view: 'channel',
      provider: 'GOOGLE_ADS',
      segment: 'SEARCH',
      orgUnitIds: viewableMarkets.map( m => m.id ),
      orgUnitId: undefined,
    }
  }else{
    defaultBenchmarkingState = {
      ...defaultBenchmarkingState,
      view: 'market',
      provider: undefined,
      segment: undefined,
      orgUnitIds: undefined,
      orgUnitId: defaultOrgUnitId
    } as MarketViewBenchmarkingState
  }

  const [state, setState] = useUrlState<BenchmarkingState>(defaultBenchmarkingState)

  useEffect(() => {
    if( maxDate && !(state.comparisonStart || state.comparisonEnd || state.baselineEnd || state.baselineStart) ){
      setState( prev => ({
        ...prev,
        ...getComparisonParams(getDefaultPeriod(maxDate))
      }))
    }
  }, [
    maxDate,
    setState,
    state.comparisonStart,
    state.comparisonEnd,
    state.baselineStart,
    state.baselineEnd,
  ])

  const [monthPickerVariant, setMonthPickerVariant] = useState<MonthPickerProps['variant']>('financial')

  const { view } = state

  const dateHookParams: Pick<ProviderBenchmarksHookProps, 'baselineEnd' | 'baselineStart' | 'comparisonStart' | 'comparisonEnd'> = {
    baselineStart: stringToDate(state.baselineStart),
    baselineEnd: stringToDate(state.baselineEnd),
    comparisonStart: stringToDate(state.comparisonStart),
    comparisonEnd: stringToDate(state.comparisonEnd),
  }

  const benchmarkQuery = useProviderBenchmarks({
    provider: state.provider as ReportProviderV2,
    orgUnitIds: state.orgUnitIds as string[],
    ...dateHookParams,
    enabled: view === 'channel',
  })

  const marketBenchmarkQueries = useAllProviderBenchmarks({
    orgUnitId: state.orgUnitId as string,
    ...dateHookParams,
    enabled: view === 'market',
  })

  const benchmarkData = benchmarkQuery.data || null

  const marketBenchmarkData = marketBenchmarkQueries.reduce( (acc, q) => {
    let { data } = q
    if( data ){
      const segmentExclusions = segmentExclusionMap[data.provider] || []
      if( segmentExclusions.length ){
        data = {
          ...data,
          results: data.results.filter( r => !segmentExclusions.includes(r.provider_segment.segment)),
        }
      }
    }
    acc.push(data)
    return acc
  }, [] as (ProviderBenchmarks | undefined)[])

  const benchmarkDatasets = useMemo(() => {
    if( benchmarkData ){
      return getBenchmarkDatasets(benchmarkData)
    }
    return null
  }, [benchmarkData])

  const providerSegmentsRef = useRef<Partial<Record<ReportProviderV2, string[]>>>({})

  if( benchmarkData && view === 'channel' ){
    providerSegmentsRef.current[state.provider] = benchmarkData.results.map( r => r.provider_segment.segment )
  }

  const availableSegments = view === 'channel' && providerSegmentsRef.current[state.provider] || []


  const defaultSegment = (
    view === 'channel' && availableSegments && availableSegments.includes(defaultProviderSegmentMap[state.provider] as string) ?
      defaultProviderSegmentMap[state.provider] as string :
      availableSegments[0]
  )

  const canonicalSegment = (
    state.segment && availableSegments.includes(state.segment) ?
      state.segment :
      defaultSegment
  )

  useEffect(() => {
    if( view === 'channel' && canonicalSegment && state.segment !== canonicalSegment ){
      setState( prev => ({ ...prev, segment: canonicalSegment }) as ChannelViewBenchmarkingState)
    }
  }, [canonicalSegment, setState, state.segment, view])

  let activeBenchmarkData: ProviderBenchmarks[] | null = null
  if( view === 'channel' && canonicalSegment ){
    activeBenchmarkData = benchmarkData && [
      {
        ...benchmarkData,
        results: benchmarkData.results.filter( d => d.provider_segment.segment === canonicalSegment )
      }
    ]
  }
  if( view === 'market' && every(marketBenchmarkData) ){
    activeBenchmarkData = marketBenchmarkData as ProviderBenchmarks[]
  }

  const appTheme = useAppTheme()

  const benchmarkChartData = activeBenchmarkData && getBenchmarkChartData(activeBenchmarkData, appTheme)

  const { rows, metrics } = getBenchmarkingTableRows(activeBenchmarkData || [], view === 'market')

  const costEffectivenessMetric = metrics.length === 1 ? metrics[0] : null

  const filterState: BenchmarkingFilters = urlStateToBenchmarkingFilters(state)

  let titleFragments: (string | null)[] = [
    'Reporting',
    'Benchmarking',
  ]

  let datasetLabel: string | null = null

  if( view === 'channel' ){
    const providerLabel = providerLabelMap[state.provider]
    const segmentLabel = state.segment && getSegmentLabel(state.segment)
    titleFragments = [
      ...titleFragments,
      providerLabel,
      segmentLabel,
    ]
    datasetLabel = `${providerLabel} - ${segmentLabel}`
  }else{
    const market = state.orgUnitId && vodafoneMarketsById[state.orgUnitId]
    if( market ){
      titleFragments.push(market.name)
      datasetLabel = market.name
    }
  }

  titleFragments.push(timePeriodsToPageTitleFragment(filterState.timePeriods))

  useDocumentTitle({
    paths: titleFragments,
  })

  const classes = useStyles()

  const breakpoint = useBreakpoint()

  const markerSize = breakpointMarkerSizes[breakpoint] || 16

  const chartSize = breakpointChartHeights[breakpoint] || 400

  const activeQueries: UseQueryResult[] = (
    view === 'channel' ? [benchmarkQuery] : marketBenchmarkQueries
  )

  const isLoading = some(activeQueries, q => q.isLoading )
  const isError = some(activeQueries, q => q.isError )

  const excludedSegments = state.provider && segmentExclusionMap[state.provider]

  return (
    <Box p={3}>
      <Typography variant='h3' className={classes.title}>
        Benchmarking
      </Typography>

      <Card
        className={classes.filterCard}>
        <FilterPanel
          values={filterState}
          onConfirm={(values): void => {
            setState(benchmarkingFiltersToUrlState(values))
          }}
          validate={(values): boolean => {
            return !!(
              values.timePeriods
              && (
                values.view === 'channel' ?
                  values.orgUnitIds && values.orgUnitIds.length :
                  !!values.orgUnitId
              )
            )
          }}
          displayConfig={{
            /* eslint-disable react/display-name, react/prop-types */
            timePeriods: {
              label: 'Time Periods',
              nullable: false,
              render: ({ baseline, comparison }: TimePeriods): JSX.Element | null => {
                if( !every([...baseline, ...comparison]) ){
                  return null
                }
                return (
                  <>
                    { nullableDateRangeToDisplayString(comparison) }
                    <span style={{marginLeft: 4, marginRight: 4, opacity: 0.65}}>vs</span>
                    { nullableDateRangeToDisplayString(baseline) }
                  </>
                )
              },
              TypographyProps: {
                style: {
                  minWidth: 'max-content',
                },
              },
            },
            view: {
              label: 'View',
              nullable: false,
              render: (view: string): JSX.Element => <>{capitalize(view)}</>,
            },
            orgUnitIds: {
              label: 'Markets',
              render: (orgUnitId: string): JSX.Element => (
                <MarketDisplayLabel {...vodafoneMarketsById[orgUnitId]} />
              ),
            },
            orgUnitId: {
              label: 'Market',
              nullable: false,
              render: (orgUnitId: string): JSX.Element => (
                <MarketDisplayLabel {...vodafoneMarketsById[orgUnitId]} />
              ),
            },
            /* eslint-enable react/display-name, react/prop-types */
          }}
          valueOrder={['timePeriods', 'view', 'orgUnitId', 'orgUnitIds']}>
          { ({ values, updateValues }): JSX.Element => {
            return (
              <Grid container spacing={5}>
                { values.timePeriods && (
                  <Grid item xs='auto' className={classes.monthRangeInput}>
                    <FormLabel>Time Periods</FormLabel>

                    <Box display='flex' alignItems='center' mt={1} pl={2}>
                      <Box mr={2}>
                        <FormLabel>View</FormLabel>
                      </Box>
                      <RadioFormGroup
                        value={monthPickerVariant}
                        RadioProps={{
                          size: 'small',
                        }}
                        FormGroupProps={{row: true}}
                        options={[
                          {
                            value: 'financial',
                            label: 'Financial Year',
                          },
                          {
                            value: 'calendar',
                            label: 'Calendar Year',
                          }
                        ]}
                        onChange={setMonthPickerVariant}>
                      </RadioFormGroup>
                    </Box>

                    { minDate && maxDate && (
                      <TimeComparisonFilterInput
                        value={values.timePeriods}
                        minDate={minDate}
                        maxDate={dateFnsOps.endOfMonth(maxDate)}
                        monthPickerVariant={monthPickerVariant}
                        TypographyProps={{
                          variant: 'subtitle2',
                          color: 'textSecondary',
                        }}
                        comparisonEnabled={true}
                        comparisonOptional={false}
                        onChange={(timePeriods): void => {
                          updateValues({ timePeriods })
                        }} />
                    )}
                  </Grid>
                )}

                <Grid item xs='auto'>
                  <RadioFormGroup
                    name='Segmentation'
                    value={values.view}
                    RadioProps={{
                      size: 'small',
                    }}
                    options={[
                      {
                        value: 'channel',
                        label: 'Channel',
                      },
                      {
                        value: 'market',
                        label: 'Market',
                      }
                    ]}
                    onChange={(view: 'channel' | 'market'): void => {
                      updateValues({ view })
                    }}>
                  </RadioFormGroup>
                </Grid>

                { values.view === 'channel' && (
                  <Grid item xs='auto'>
                    <CheckboxGroup
                      name='Markets'
                      FormLabelProps={{
                        error: !values.orgUnitIds || !values.orgUnitIds.length,
                      }}
                      value={values.orgUnitIds}
                      options={
                        viewableMarkets.map( option => ({
                          value: option.id,
                          label: <MarketDisplayLabel {...option} />,
                        }))
                      }
                      onChange={(orgUnitIds): void => updateValues({ orgUnitIds })} />
                  </Grid>
                )}

                { values.view === 'market' && (
                  <Grid item xs='auto'>
                    <RadioFormGroup
                      name='Market'
                      value={values.orgUnitId}
                      FormLabelProps={{
                        error: !values.orgUnitId,
                      }}
                      options={
                        viewableMarkets.map( option => ({
                          value: option.id,
                          label: <MarketDisplayLabel {...option} />,
                        }))
                      }
                      onChange={(orgUnitId): void => updateValues({ orgUnitId })} />
                  </Grid>
                )}

              </Grid>
            )
          }}
        </FilterPanel>

      </Card>

      { view === 'channel' && (
        <>
        <BenchmarkingTabs
          value={state.provider}
          onChange={(provider): void => setState( prev => ({ ...prev, provider }) as ChannelViewBenchmarkingState)} />

        <Box display='flex' justifyContent='center'>
          <Tabs
            value={state.segment}
            onChange={(_, value): void => {
              setState( prev => ({ ...prev, segment: value }))
            }}>
            { (benchmarkDatasets || []).filter( d => !excludedSegments || !excludedSegments.includes(d.key) ).map( dataset => (
              <Tab
                key={dataset.key}
                value={dataset.key}
                label={dataset.label}
                className={classes.tab} />
            ))}
          </Tabs>
        </Box>
        </>
      )}

      { isLoading && (
        <Loader preset='centered' minHeight='20rem' />
      )}
      
      { isError && (
        <Box
          p={12}
          fontSize={22}
          color='text.disabled'
          display='flex'
          justifyContent='center'>
          <strong>
            An error occurred and benchmarking data could not be loaded
          </strong>
        </Box>
      )}

      { benchmarkChartData && benchmarkChartData.length === 0 && (
        <Box
          display='flex'
          justifyContent='center'
          fontSize={20}
          color='text.secondary'>
          No data available for this platform with the supplied filters
        </Box>
      )}

      {/* <Grid container justify='space-evenly'>
          { activeDatasets.map( dataset => (
            <Grid
              key={dataset.label}
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}>
              <Box mx={8} my={4}>
                <ExportableBenchmarkQuadrantChart
                  title={
                    <Typography
                      className={classes.datasetLabel}
                      align='center'
                      variant='h4'>
                      { dataset.label }
                    </Typography>
                  }
                  downloadName={
                    [
                      dataset.label,
                      formatUTC(previousDate, 'MMM-yy'),
                      formatUTC(currentDate, 'MMM-yy'),
                    ].join('.')
                  }
                  benchmarkData={dataset.data}
                  costEffectivenessMetric={dataset.costEffectivenessMetric}
                  markerSize={markerSize} />
              </Box>
            </Grid>
          ))}
        </Grid> */}

      <Grid className={classes.gridContainer} container spacing={5}>
        { benchmarkChartData && (
          <Grid item xs={12} md='auto'>
            <Card>
              <div className={classes.chartContainer}>
                <div className={classes.chart}>
                  <ExportableBenchmarkQuadrantChart
                    key={String(datasetLabel)}
                    size={chartSize}
                    title={
                      datasetLabel && (
                        <Typography
                          className={classes.datasetLabel}
                          align='center'
                          variant='h4'>
                          { datasetLabel }
                        </Typography>
                      )
                    }
                    downloadName={
                      [
                        datasetLabel,
                        `Baseline(${state.baselineStart}.${state.baselineEnd})`,
                        `Comparison(${state.comparisonStart}.${state.comparisonEnd})`,
                      ].join('.')
                    }
                    benchmarkData={benchmarkChartData}
                    costEffectivenessMetric={costEffectivenessMetric}
                    getTooltipContent={
                      view === 'channel' ?
                        getMarketTooltipContent :
                        null
                    }
                    datumRenderer={view === 'channel' ? MarketDatumRenderer : DefaultDatumRenderer}
                    showLegend={view === 'market'}
                    getBenchmarkDatumLabel={getDatumLabel}
                    markerSize={markerSize} />
                </div>
              </div>
            </Card>
          </Grid>
        )}

        { activeBenchmarkData && (
          <Grid className={classes.tableGridItem} item xs={12} md='auto'>
            <Card>
              <BenchmarkingTable
                rows={rows}
                costEffectivenessMetric={costEffectivenessMetric} />
            </Card>
          </Grid>
        )}
      </Grid>

    </Box>
  )
}
